import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { request } from "./api";

export default function ClientSearch({ onSelect, disabled, purpose = "sighting" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState("");
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const sequence = useRef(0);
  const id = `ted-search-${purpose}`;
  useEffect(() => {
    const version = ++sequence.current;
    setResults([]);
    setActive(-1);
    setError("");
    setSearching(!!query.trim());
    if (!query.trim()) return;
    const timer = setTimeout(
      () =>
        request(
          `search?q=${encodeURIComponent(query)}${purpose === "history" ? "&mode=history" : ""}`
        )
          .then((rows) => {
            if (version === sequence.current) {
              setResults(rows);
              setSearching(false);
            }
          })
          .catch((e) => {
            if (version === sequence.current) {
              setError(e.message);
              setSearching(false);
            }
          }),
      200
    );
    return () => {
      clearTimeout(timer);
      sequence.current++;
    };
  }, [query, purpose]);
  const choose = (client) => {
    if (disabled || (purpose !== "history" && (client.deleted || client.deceased))) return;
    onSelect(client);
    setQuery("");
    setOpen(false);
  };
  return (
    <div
      className="cb-search"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <label className="cb-search__field" htmlFor={id}>
        <span className="ccw-visually-hidden">
          {purpose === "sighting" ? "Search clients by name or ID" : "Choose client by name or ID"}
        </span>
        <input
          id={id}
          value={query}
          placeholder="Search by client name or ID"
          autoComplete="off"
          role="combobox"
          aria-expanded={open && !!query}
          aria-controls={`${id}-results`}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${id}-${active}` : undefined}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (["ArrowDown", "ArrowUp"].includes(e.key) && results.length) {
              e.preventDefault();
              setOpen(true);
              setActive((previous) => {
                if (previous < 0) return e.key === "ArrowDown" ? 0 : results.length - 1;
                return (
                  (previous + (e.key === "ArrowDown" ? 1 : -1) + results.length) % results.length
                );
              });
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (open && active >= 0) choose(results[active]);
            }
          }}
        />
      </label>
      {open && query && (
        <div
          className="cb-search__results"
          role="listbox"
          id={`${id}-results`}
          aria-label="Matching clients"
        >
          {results.map((client, index) => (
            <button
              type="button"
              className={`cb-result ${active === index ? "is-active" : ""}`}
              role="option"
              aria-selected={active === index}
              id={`${id}-${index}`}
              key={client.id}
              disabled={disabled || (purpose !== "history" && (client.deceased || client.deleted))}
              onClick={() => choose(client)}
            >
              <span className="cb-result__info">
                <strong>{client.name}</strong>
                <span>
                  ID {client.id} · Born {client.born} · {client.residence}
                </span>
              </span>
              <span className="cb-result__tags">
                {client.deleted
                  ? "Deleted profile · retained TED history"
                  : client.deceased
                  ? "Deceased"
                  : client.follow_up === "archived"
                  ? "Archived follow-up"
                  : client.inactive
                  ? "Inactive profile"
                  : ""}
                <span className="cb-result__action">
                  {purpose === "sighting" ? "Log as seen" : "Select"}
                </span>
              </span>
            </button>
          ))}
          {!results.length && (
            <p className="cb-search__empty">
              {error || (searching ? "Searching…" : "No clients match. Try a name or exact ID.")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
ClientSearch.propTypes = {
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  purpose: PropTypes.string
};
